import { Tab, Tabs } from '@mui/material';
import { JobData } from 'components/JobQueue/utils';
import { Loader } from 'components/loader';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getAllListData } from 'services/CommonServices';
import { MASTER_ENDPOINT } from 'utils/Constant';

import { CandidateHeader } from './components/CandidateHeader';
import { TabContent } from './components/TabContent';
import { PdfViewer } from './viewer';

type Props = {
  visible?: boolean;
  onClose?: () => void;
  resumeId?: string;
  fileId?: string;
  candidateId?: string;
  candidateData?: any;
  jobData?: any;
  fileName?: string;
  showDownload?: boolean;
  showShortlist?: boolean;
  onDownload?: () => void;
  onShortlist?: () => void;
  widthType?: string;
  jobId?: string;
};

export const IPriseJobsPreview: React.FC<Props> = (props: Props) => {
  const location = useLocation();
  const candidateDetails = location?.state?.candidateDetails;
  const {
    visible = true,
    // onClose,
    // fileId: propFileId,
    candidateId,
    candidateData: propCandidateData,
    jobData: propJobData,
    fileName,
    showDownload = true,
    showShortlist = false,
    onDownload,
    onShortlist,
    widthType,
    jobId: propJobId,
  } = props;

  const {
    jobId: paramJobId,
    candidateId: paramCandidateId,
    // fileId: paramFileId,
  } = useParams<{
    jobId?: string;
    candidateId?: string;
    fileId?: string;
  }>();

  const [activeTab, setActiveTab] = useState(0);
  const [candidateData, setCandidateData] = useState<any>(
    propCandidateData || {},
  );
  const [jobData, setJobData] = useState<JobData>(propJobData || {});
  const [loader, setLoader] = useState<boolean>(
    !propCandidateData && !propJobData,
  );

  // Use jobId and candidateId from props or params
  const jobId = propJobId || paramJobId;
  const finalCandidateId = candidateId || paramCandidateId;

  // Use fileId from props or params
  // const fileId = propFileId || paramFileId || '';

  const getCandidateData = useCallback(async (id: string) => {
    if (!id) return;

    setLoader(true);
    await getAllListData(`${MASTER_ENDPOINT.Candidates}?id=${id}`)
      .then((res) => {
        setCandidateData(res?.data?.[0] || {});
      })
      .catch((err: any) => {
        console.error('Error fetching candidate data:', err);
      })
      .finally(() => {
        setLoader(false);
      });
  }, []);

  const getJobData = useCallback(
    async (id: string) => {
      if (!id || propJobData) return;

      await getAllListData(`${MASTER_ENDPOINT.Jobs}?id=${id}`)
        .then((res) => {
          setJobData(res?.data?.[0] || {});
        })
        .catch((err: any) => {
          console.error('Error fetching job data:', err);
        });
    },
    [propJobData],
  );

  useEffect(() => {
    if (finalCandidateId && !propCandidateData) {
      getCandidateData(finalCandidateId);
    } else if (propCandidateData) {
      setCandidateData(propCandidateData);
      setLoader(false);
    }
    if (jobId && !propJobData) {
      getJobData(jobId);
    } else if (propJobData) {
      setJobData(propJobData);
    }
  }, [
    finalCandidateId,
    jobId,
    propCandidateData,
    propJobData,
    getCandidateData,
    getJobData,
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (!visible) return null;

  // Format candidate data for header
  const candidateName =
    candidateDetails?.candidate?.firstName &&
    candidateDetails?.candidate?.lastName
      ? `${String(candidateDetails.candidate.firstName)} ${String(
          candidateDetails.candidate.lastName,
        )}`
      : candidateDetails?.name;
  const candidateInternalId = candidateDetails?.candidate?.id || '';

  const availability =
    candidateDetails?.candidate?.benchStatus ||
    candidateDetails?.candidate?.applicantStatus ||
    'Not Available';
  const availabilityDate = candidateDetails?.candidate?.benchAvailability
    ? new Date(
        String(candidateDetails.candidate.benchAvailability),
      ).toLocaleDateString('en-GB')
    : undefined;

  const candidateEmail = candidateDetails?.candidate?.email || '';
  const candidatePhone =
    candidateDetails?.candidate?.phone ||
    candidateDetails?.candidate?.mobileNumber ||
    '';
  const candidateLocation =
    candidateDetails?.candidate?.city && candidateDetails?.candidate?.state
      ? `${String(candidateDetails.candidate.city)}, ${String(
          candidateDetails.candidate.state,
        )}`
      : candidateDetails?.location || '';

  const matchScore =
    candidateDetails?.matchPercentage || candidateDetails?.matchScore;
  const visaStatus =
    candidateDetails?.workStatus?.name || candidateDetails?.workAuth || '';
  // Calculate pay rate from job or candidate
  const payRate = ((): string => {
    if (candidateData?.payRate) return `$${String(candidateData.payRate)}`;
    if (jobData?.payRangeFrom && jobData?.payRangeTo) {
      return `$${jobData.payRangeFrom} - $${jobData.payRangeTo}`;
    } else if (jobData?.payRangeFrom) {
      return `$${jobData.payRangeFrom}`;
    }
    return '';
  })();

  const source = candidateData?.source || jobData?.jobSource || '';

  const defaultLayout = [
    widthType === 'Sales'
      ? 50
      : widthType === 'recruiterRead' || widthType === 'recruiter'
      ? 45
      : 50,
    widthType === 'Sales'
      ? 50
      : widthType === 'recruiterRead' || widthType === 'recruiter'
      ? 55
      : 50,
  ];

  if (loader) {
    return (
      <div
        style={{
          alignItems: 'center',
          backgroundColor: '#ffffff',
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
          width: '100%',
        }}>
        <Loader newLoader />
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        display: 'flex',
        height: 'calc(100vh - 70px)',
        overflow: 'hidden',
        width: '100%',
      }}>
      {/* Left Side */}
      <div
        style={{
          borderRight: '1px solid #e5e7eb',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          width: `${defaultLayout[0]}%`,
        }}>
        {/* Header Section */}
        <CandidateHeader
          candidateName={candidateName}
          availability={availability}
          availabilityDate={availabilityDate}
          email={candidateEmail}
          phone={candidatePhone}
          location={candidateLocation}
          matchScore={matchScore}
          visaStatus={visaStatus}
          payRate={payRate}
          source={source}
          jobTitle={jobData?.jobTitle}
          showDownload={showDownload}
          showShortlist={showShortlist}
          onDownload={onDownload}
          onShortlist={onShortlist}
          onShortlistForJob={onShortlist}
          candidateInternalId={candidateInternalId}
        />

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .Mui-selected': {
              color: '#000000 !important',
            },
            '& .MuiTab-root': {
              color: '#6b7280',
              fontFamily: 'Red Hat Display Medium',
              fontSize: '13px',
              fontWeight: 400,
              minHeight: '48px',
              textTransform: 'none',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#ef4444',
              height: '3px',
            },
            borderBottom: '1px solid #e5e7eb',
            paddingLeft: '24px',
          }}>
          <Tab label="Overview" />
          <Tab label="Notes" />
          <Tab label="Attachments" />
        </Tabs>

        {/* Tab Content */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <TabContent
            activeTab={activeTab}
            candidateData={candidateDetails || candidateData}
            candidateId={finalCandidateId}
            jobData={jobData}
          />
        </div>
      </div>

      {/* Right Side - PDF Viewer */}
      <div
        style={{
          backgroundColor: '#f9fafb',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          width: `${defaultLayout[1]}%`,
        }}>
        <PdfViewer
          // url={`${process.env.REACT_APP_APIURL}${MASTER_ENDPOINT.Files}/${fileId}?type=pdf`}
          url={
            'https://ontheline.trincoll.edu/images/bookdown/sample-local-pdf.pdf'
          }
          hideDownload={true}
          fileName={fileName}
          candidateId={finalCandidateId}
        />
      </div>
    </div>
  );
};
